import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { BsEyeFill } from 'react-icons/bs'
import { addToCart } from '../action'

function Product({ product }) {
  const dispatch = useDispatch()
  const { id, title, description, category, price, currency, imageUrl, imageUrls, stock } = product
  const resolvedCurrency = currency || 'ARS'
  const displayPrice = Number(price)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Obtener carrito del store y buscar si el producto ya está ahí
  const cartItems = useSelector(state => state.cart)
  const cartItem = cartItems.find(item => item.id === id)
  const quantityInCart = cartItem ? cartItem.quantity : 0

  const handleCart = () => {
    if (stock > quantityInCart) {
      dispatch(addToCart(product))
    } else {
      alert('No hay suficiente stock disponible para agregar más unidades')
    }
  }

  const images = useMemo(() => {
    if (imageUrls && imageUrls.length > 0) return imageUrls
    if (imageUrl) return [imageUrl]
    return ['https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=400']
  }, [imageUrl, imageUrls])

  const getImageSrc = (url) => {
    if (!url) return 'https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=400'
    return url.startsWith('http') ? url : `http://localhost:4200${url}`
  }

  const goNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const goPrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  useEffect(() => {
    setImageLoaded(false)
  }, [currentImageIndex])

  useEffect(() => {
    images.forEach((url) => {
      const img = new Image()
      img.src = getImageSrc(url)
    })
  }, [images])

  return (
    <div className="vintage-product-card group">
      {/* Badge de estado */}
      {stock === 0 && (
        <div className="vintage-badge out-of-stock">
          Sin Stock
        </div>
      )}
      {stock > 0 && stock <= 3 && (
        <div className="vintage-badge">
          Últimas {stock}
        </div>
      )}

      {/* Imagen del producto */}
      <div className="relative h-[260px] sm:h-[320px] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
        <img
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 card-image-fade ${
            imageLoaded ? 'is-loaded' : ''
          }`}
          src={getImageSrc(images[currentImageIndex])}
          alt={title}
          decoding="async"
          loading="eager"
          onLoad={() => setImageLoaded(true)}
        />

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={goPrevImage}
              className="card-carousel-btn left-3 z-10"
              aria-label="Imagen anterior"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={goNextImage}
              className="card-carousel-btn right-3 z-10"
              aria-label="Imagen siguiente"
            >
              ›
            </button>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 z-10">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentImageIndex(index)}
                  className={`card-carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                  aria-label={`Ver imagen ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Overlay con botones */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 pointer-events-none">
          <div className="absolute top-4 left-4 flex flex-col gap-2 items-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-active:opacity-100 transition-all duration-300 pointer-events-auto">
            <button
              onClick={handleCart}
              disabled={stock <= quantityInCart}
              className="h-9 w-9 rounded-full bg-slate-800 text-white shadow-lg flex items-center justify-center hover:bg-slate-900 transition disabled:opacity-60"
              aria-label="Agregar al carrito"
            >
              +
            </button>
            <Link
              to={`/producto/${id}`}
              className="h-9 w-9 rounded-full bg-white text-slate-800 shadow-lg flex items-center justify-center hover:bg-slate-100 transition"
              aria-label="Ver producto"
            >
              <BsEyeFill className="text-sm" />
            </Link>
          </div>
        </div>

        {/* Indicador de múltiples imágenes */}
        {images.length > 1 && (
          <div className="absolute top-3 right-3 bg-white bg-opacity-90 text-slate-700 text-xs px-2 py-1 rounded backdrop-blur-sm">
            {currentImageIndex + 1}/{images.length}
          </div>
        )}
      </div>
      {/* Información del producto */}
      <div className="p-5 space-y-3">
        {/* Categoría */}
        <div className="vintage-category">
          {category?.name || 'Vintage'}
        </div>

        {/* Título */}
        <Link to={`/producto/${id}`}>
          <h3 className="vintage-title text-lg leading-tight">
            {title}
          </h3>
        </Link>

        {/* Precio */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="price-old">
              {resolvedCurrency === 'USD' ? 'US$' : '$'}{Number(displayPrice * 2).toLocaleString('es-AR')}
            </span>
            <span className="vintage-price">
              {resolvedCurrency === 'USD' ? 'US$' : '$'}{Number(displayPrice).toLocaleString('es-AR')}
            </span>
            <span className="text-xs text-slate-500 mt-1">{resolvedCurrency}</span>
          </div>
          
          {/* Indicador de stock visual */}
          <div className="flex items-center gap-1">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i < Math.min(stock, 3) 
                    ? stock > 5 ? 'bg-green-500' : stock > 2 ? 'bg-yellow-500' : 'bg-red-500'
                    : 'bg-stone-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Descripción corta si existe */}
        {description && (
          <p className="text-sm text-stone-600 line-clamp-2 leading-relaxed">
            {description.length > 80 ? `${description.slice(0, 80)}...` : description}
          </p>
        )}
      </div>
    </div>
  )
}

export default Product
